/**********************************************************
* A notification can mean one of several things
*
* - An "alert", which is an individually closeable message
*   that can take one of several "severity" levels, and
*   may have some optional action buttons or links to
*   relevant information.
* - A "job" which is a long-running task that usually has
*   a progress bar, and cannot be directly closed but must
*   be canceled. Canceling the job retains an alert that
*   can then be closed.
*
*  Notifications are combined into a tray that the user
*  can view, and individually action on different alerts.
*  A new notification will cause the try to briefly open
*  and for that alert to be visible.
*
/*********************************************************/

import { GlobalComposable } from "~/classes/composable/GlobalComposable"

class NotificationTray extends GlobalComposable<{
  isOpen: boolean
  isPeek: boolean
  peekTimeout?: any
  showRipple: boolean
  rippleTimeout?: any
}> {
  #JOB_COMPLETED_TIMEOUT_MS = 2000

  override defaultState = () => ({
    isOpen: false,
    isPeek: false,
    showRipple: false,
  })

  openNotificationTray = () => {
    this.state.value.isOpen = true
    this.state.value.isPeek = false
  }

  /**
   * Gets an ordered list of all alerts and jobs, sorted in descending
   * order by timestamp (latest first).
   */
  private _sortedNotifications = (): AnyNotification[] => {
    const { state: jobState } = useJobs()
    const { state: alertState } = useAlerts()

    return [
      ...Object.values(alertState.value.alerts),
      ...Object.values(jobState.value.jobs),
    ].sort((a, b) => b.timestamp - a.timestamp)
  }

  sortedNotifications = computed(this._sortedNotifications)
  latestNotification = computed(() => first(this._sortedNotifications()))

  clearNotificationById = (id: string) => {
    const { clearJobById } = useJobs()
    const { clearAlert } = useAlerts()

    clearJobById(id)
    clearAlert(id)

    if (len(this.sortedNotifications.value) === 0)
      this.closeNotificationTray()
  }

  clearNotifications = () => {
    const { clearAllJobs } = useJobs()
    const { clearAllAlerts } = useAlerts()

    clearAllJobs()
    clearAllAlerts()

    this.closeNotificationTray()
  }

  closeNotificationTray = () => {
    this.state.value.isOpen = false
  }

  /**
   * We show the progress bar if there are any jobs at all.
   */
  hasProgress = () => {
    const { state: jobState } = useJobs()
    return len(jobState.value.jobs) > 0
  }

  progressFraction = () => {
    const { progressDetails } = useJobs()
    let completeOrFailedBytesTotal = 0
    let totalBytesTotal = 0

    for (const job of Object.values(useJobs().state.value.jobs)) {
      const details = progressDetails(job.transferId)
      // sorta confusing, but if the uploaded "finishedBytes" is greater than the
      // sum of failed and success bytes, then we use that, otherwise we use the sum
      // of failed and success bytes
      const bytes = Math.max(details.finishedBytes, details.failedBytes + details.successBytes)
      completeOrFailedBytesTotal += bytes
      totalBytesTotal += details.totalBytes
    }

    if (totalBytesTotal === 0)
      return 0
    return completeOrFailedBytesTotal / totalBytesTotal
  }

  /**
   * When a job completes, we set a timeout to auto-hide the tray, so long as
   * this is the latest notification and there are no other jobs in progress
   */
  onJobCompleted = (id: string) => {
    const { anyJobsInProgress } = useJobs()

    setTimeout(() => {
      if (!this.state.value.isOpen)
        return
      if (anyJobsInProgress.value)
        return
      if (id !== this.latestNotification.value?.transferId)
        return // the later notification will take care of this
      this.closeNotificationTray()
    }, this.#JOB_COMPLETED_TIMEOUT_MS)
  }

  /**
   * Shows the "peek" view of the notification and a ripple for any errors. Should not be
   * called unless we know there is something to show.
   */
  showNotificationAnimations = () => {
    this.state.value.showRipple = true
    clearTimeout(this.state.value.rippleTimeout)
    this.state.value.rippleTimeout = setTimeout(() => {
      this.state.value.showRipple = false
    }, 1500)

    this.state.value.isPeek = true
    this.state.value.isOpen = true
    clearTimeout(this.state.value.peekTimeout)
    this.state.value.peekTimeout = setTimeout(() => {
      if (this.state.value.isPeek && this.state.value.isOpen) {
        this.closeNotificationTray()
      }
    }, 3000)
  }
}

const notifications = new NotificationTray("notification-tray", theme.colors.green.hex)

export default function () {
  return notifications.use()
}
